import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, MapPinOff } from "lucide-react";

import { resolveJls } from "@/api/client";
import { Button } from "@/components/ui/button";
import { PlanUploadDialog } from "./PlanUploadDialog";

/**
 * R1 + R4 — plan-mismatch guard, UI half + the plan-upload entry point.
 *
 * Shown on Provjeri when the project's municipality can't be keyed to a
 * spatial plan in the base (no explicit municipality AND the katastarska
 * općina doesn't resolve via the backend k.o.→JLS resolver). The banner
 * warns that plansko-pravni navodi will carry the placeholder, and — R4 —
 * opens the plan-upload wizard right here (prefilled with the k.o.),
 * writes the activated municipality onto the project, and offers to
 * regenerate.
 */
export function PlanMissingBanner({
  municipalityCode,
  katastarskaOpcina,
  projectId,
  onPlanActivated,
}: {
  municipalityCode?: string | null;
  katastarskaOpcina?: string | null;
  /** Project the activated municipality is written onto (R4). */
  projectId?: string;
  /** Fired after a plan is activated — the parent offers regeneration
   *  (e.g. re-enable / highlight the Generiraj CTA). */
  onPlanActivated?: (code: string, name: string) => void;
}) {
  const ko = (katastarskaOpcina ?? "").trim();
  const hasMunicipality = Boolean((municipalityCode ?? "").trim());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activated, setActivated] = useState<{ name: string } | null>(null);

  const { data } = useQuery({
    queryKey: ["jls-resolve", ko],
    queryFn: () => resolveJls(ko),
    // Only ask when it can change the outcome: no explicit municipality
    // but we do have a k.o. name to try.
    enabled: !hasMunicipality && ko.length > 0,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  // A plan was just activated in-session → show the success + regen prompt
  // instead of the missing banner, even before the draft round-trips.
  if (activated) {
    return (
      <div className="mb-3 flex flex-wrap items-center gap-3 rounded-md border border-green-300 bg-green-50 px-4 py-3 text-[0.85rem] text-green-900">
        <CheckCircle2 className="h-4 w-4 shrink-0 text-green-700" />
        <span className="min-w-0 flex-1">
          Plan za <strong>{activated.name}</strong> je aktivan — klikni{" "}
          <strong>Generiraj</strong> da se dokumenti ponovno izrade s citatima
          plana.
        </span>
      </div>
    );
  }

  if (hasMunicipality) return null;
  if (!ko) return null;
  // Unresolved query (loading/error) stays quiet — the backend enforces the
  // guard regardless; this banner is advance warning, not the safety net.
  if (!data || data.slug) return null;

  return (
    <>
      <div className="mb-3 flex flex-wrap items-start gap-3 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-[0.85rem] text-amber-900">
        <MapPinOff className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" />
        <div className="min-w-0 flex-1">
          <p className="font-semibold">
            Prostorni plan za k.o. {ko} nije u Permitko bazi.
          </p>
          <p className="mt-1 text-amber-800">
            Plansko-pravni navodi u dokumentima dobit će oznaku{" "}
            <span className="font-mono text-[0.78rem]">
              [PLAN ZA k.o. {ko} NIJE U BAZI — priložite prostorni plan]
            </span>{" "}
            umjesto citata plana — nikad se ne citira plan druge općine.
          </p>
          <div className="mt-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="border-amber-400 text-amber-900 hover:bg-amber-100"
              onClick={() => setDialogOpen(true)}
            >
              Učitaj prostorni plan
            </Button>
          </div>
        </div>
      </div>

      <PlanUploadDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        projectId={projectId}
        initialJlsName={ko}
        onActivated={(code, name) => {
          setActivated({ name });
          setDialogOpen(false);
          onPlanActivated?.(code, name);
        }}
      />
    </>
  );
}
